import {
    Checklist as ChecklistIcon,
    ContentCopy as ContentCopyIcon,
    DriveFileMoveOutlined as DriveFileMoveOutlinedIcon,
    MoneyOffRounded as MoneyOffRoundedIcon,
    UpdateRounded as UpdateRoundedIcon,
} from 'components/icons';
import CancelIcon from 'assets/images/icons/CancelIcon.svg?react';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal.tsx';
import SwitchMatterModal, { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util';
import React, { useMemo } from 'react';
import { Control, UseFormGetValues, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { useDeleteInvoiceExpensesMutation } from 'store/api/invoice-details.ts';
import { FeatureFlag } from 'types/features.ts';
import { InvoiceExpenseForm, InvoiceMatterForm } from 'types/invoice-details';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { ItemActionButton, ItemActionGroup } from './ItemActionButton';
import { DIALOG_560 } from 'components/modals/dialogWidths';
import { FormattedDate } from 'components/misc/FormattedDate';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { DISCARD_LIST_CLASS, DISCARD_ROW_CLASS } from './TimecardActions';

interface Props {
    handleMoveExpense: (value: SwitchMatterModalResult) => Promise<void> | void;
    handleNonBillable: () => void;
    handlePostpone: () => void;
    handleRemove: () => void;
    handleBulkSelect: () => void;
    handleDuplicate: () => void;
    control: Control<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    invoiceId: number;
    matterId: number;
    invoiceAllowsNewExpenses: boolean;
    userCanCreateExpenses: boolean;
    isCorrectionInvoice: boolean;
}

/** The selection's dialogs and their gates, which both designs' action bars open. */
export const useExpenseActions = (props: Props) => {
    const { handleMoveExpense, control, getValues, invoiceId, matterId } = props;
    const { openModal, closeModal } = useModal();
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const invoiceExpenses = useWatch({ control, name: 'invoiceExpenses' });
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const areActionsDisabled = useMemo(
        () => buttonsDisabled || !invoiceExpenses?.some((it) => it.selected),
        [invoiceExpenses, buttonsDisabled]
    );
    const isMultipleSelection = useMemo(
        () => (invoiceExpenses || []).filter((el) => el.selected).length > 1,
        [invoiceExpenses]
    );

    const openExpenseUpdateModal = (): void => {
        const expenseIds = new Set<number>(
            (getValues('invoiceExpenses') ?? []).reduce((acc: Set<number>, curr: InvoiceExpenseForm) => {
                if (curr.id && curr.selected) {
                    acc.add(curr.id);
                }

                return acc;
            }, new Set<number>([]))
        );

        const handleSwitch = async (value: SwitchMatterModalResult) => {
            await handleMoveExpense(value);
            closeModal();
        };

        openModal(
            <SwitchMatterModal
                onSubmit={handleSwitch}
                onCancel={closeModal}
                titleId={'invoices.move-to-another-case'}
                entityIds={expenseIds}
                entitySingular={t('expenses.entity-name-singular')}
                entityPlural={t('expenses.entity-name-plural')}
                showAssignmentSelect={false}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE,
            undefined,
            DIALOG_560
        );
    };

    const [deleteInvoiceExpenses] = useDeleteInvoiceExpensesMutation();

    const openBulkDiscardConfirmationModal = (): void => {
        const discarded = ((getValues('invoiceExpenses') as InvoiceExpenseForm[]) ?? []).filter(
            (invoiceExpense) => invoiceExpense.selected && invoiceExpense.expense && invoiceExpense.expense.id
        );
        const selectedExpenses = discarded.map((invoiceExpense) => invoiceExpense.expense!.id!);
        try {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        void deleteInvoiceExpenses({
                            invoiceId,
                            matterId,
                            expenseIds: selectedExpenses,
                        })
                            .then(() => successAlert(t('invoices.details.discard-expenses-succesful')))
                            .catch((err) => {
                                console.error('Error discarding expenses:', err);
                                logError(err);
                            })
                            .finally(() => closeModal());
                    }}
                    content={<p className="text-body1">{t('invoices.details.discard-expense-confirmation')}</p>}
                    alert={{
                        titleId: 'invoices.confirm.discard-expenses-title',
                        titleValues: { count: selectedExpenses.length },
                        description: (
                            <>
                                <p>{t('invoices.confirm.discard-expenses-text')}</p>
                                <ul className={DISCARD_LIST_CLASS}>
                                    {discarded.map((invoiceExpense) => (
                                        <li key={invoiceExpense.id} className={DISCARD_ROW_CLASS}>
                                            <span className="min-w-0 flex-auto">
                                                <span className="font-medium">{invoiceExpense.employee?.name}</span>
                                                <span className="text-foreground-tertiary">
                                                    {' · '}
                                                    <FormattedDate value={invoiceExpense.expenseDate} />
                                                    {invoiceExpense.expenseType
                                                        ? ` · ${invoiceExpense.expenseType.label}`
                                                        : null}
                                                </span>
                                            </span>
                                            <span className="text-foreground-secondary shrink-0 tabular-nums">
                                                <MoneyDisplay
                                                    amount={invoiceExpense.expenseAmount ?? 0}
                                                    currency={invoiceExpense.currencyType?.value}
                                                />
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ),
                        confirmId: 'invoices.tooltips.discard-expenses',
                        tone: 'danger',
                    }}
                />
            );
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const duplicateTooltip = isExpenseEntryDisabled
        ? t('expenses.integration-entry-disabled')
        : t('invoices.tooltips.duplicate');

    return {
        isExpenseEntryDisabled,
        buttonsDisabled,
        areActionsDisabled,
        isMultipleSelection,
        openExpenseUpdateModal,
        openBulkDiscardConfirmationModal,
        duplicateTooltip,
    };
};

const ExpenseActions: React.FC<Props> = (props) => {
    const {
        handleNonBillable,
        handlePostpone,
        handleRemove,
        handleBulkSelect,
        handleDuplicate,
        invoiceAllowsNewExpenses,
        userCanCreateExpenses,
        isCorrectionInvoice,
    } = props;
    const {
        isExpenseEntryDisabled,
        buttonsDisabled,
        areActionsDisabled,
        isMultipleSelection,
        openExpenseUpdateModal,
        openBulkDiscardConfirmationModal,
        duplicateTooltip,
    } = useExpenseActions(props);

    return (
        <div className="flex items-center gap-2">
            <ItemActionGroup>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.bulk-selection')}
                    onClick={handleBulkSelect}
                    disabled={buttonsDisabled}
                >
                    <ChecklistIcon className="text-[20px]" />
                </ItemActionButton>
            </ItemActionGroup>
            <ItemActionGroup>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.postpone')}
                    disabled={areActionsDisabled}
                    onClick={handlePostpone}
                >
                    <UpdateRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.mark-as-non-billable')}
                    disabled={areActionsDisabled}
                    onClick={handleNonBillable}
                >
                    <MoneyOffRoundedIcon className="text-[20px]" />
                </ItemActionButton>
                <ItemActionButton
                    tooltip={t('invoices.tooltips.move-to-another-case')}
                    disabled={areActionsDisabled}
                    onClick={openExpenseUpdateModal}
                >
                    <DriveFileMoveOutlinedIcon className="text-[20px]" />
                </ItemActionButton>
                {isCorrectionInvoice ? (
                    <ItemActionButton
                        tooltip={t('invoices.tooltips.remove-via-correction')}
                        disabled={areActionsDisabled}
                        onClick={handleRemove}
                    >
                        <CancelIcon className="size-[18px]" />
                    </ItemActionButton>
                ) : (
                    <FeatureFlagged action="show" flag={FeatureFlag.DISCARD_TIMECARDS_EXPENSES_ON_INVOICE}>
                        <ItemActionButton
                            tooltip={t('invoices.tooltips.discard-expenses')}
                            disabled={areActionsDisabled}
                            onClick={openBulkDiscardConfirmationModal}
                        >
                            <CancelIcon className="size-[18px]" />
                        </ItemActionButton>
                    </FeatureFlagged>
                )}
                {invoiceAllowsNewExpenses && (
                    <ItemActionButton
                        tooltip={duplicateTooltip}
                        label={t('invoices.tooltips.duplicate')}
                        disabled={
                            isExpenseEntryDisabled ||
                            areActionsDisabled ||
                            isMultipleSelection ||
                            !userCanCreateExpenses
                        }
                        onClick={handleDuplicate}
                    >
                        <ContentCopyIcon className="text-[20px]" />
                    </ItemActionButton>
                )}
            </ItemActionGroup>
        </div>
    );
};

export default ExpenseActions;
